export interface VideoHeroMaterial {
    img: string
    video: string
}

export interface ImageHeroMaterial {
    img: string
    alt: string
}

export interface LandingPageSeoConfig {
    key: string
    title: string
    description: string
    keywords?: string
    ogTitle?: string
    ogDescription?: string
    ogImage?: string
    ogImageAlt?: string
    twitterTitle?: string
    twitterDescription?: string
    twitterImage?: string
    twitterImageAlt?: string
    contentUrl?: string
    thumbnailUrl?: string
    breadcrumbName: string
    featureList: string[]
    howToName: string
}

export type VideoStorySectionKey =
    | 'hero'
    | 'attention'
    | 'overview'
    | 'comparison'
    | 'advantages'
    | 'testimonials'
    | 'faq'
    | 'cta'

export interface VideoStoryMedia {
    type: 'image' | 'video'
    assetKey: string
    posterKey?: string
    alt?: string
}

export interface VideoStoryAction {
    type: 'scroll-top' | 'link'
    value?: string
}

export interface VideoStoryCardItem {
    id: string
    title: string
    description?: string
    media: VideoStoryMedia
}

export interface VideoStoryComparisonColumn {
    key: string
    label: string
    labelColor?: string
}

export type VideoStoryComparisonCell =
    | string
    | {
          type: 'text'
          value: string
      }
    | {
          type: 'image'
          src: string
          alt?: string
          width?: number
          height?: number
      }
    | {
          type: 'svg'
          markup: string
          label?: string
      }

export interface VideoStoryComparisonRow {
    id: string
    label: string
    description?: string
    cells: Record<string, VideoStoryComparisonCell>
}

export interface VideoStoryAdvantageItem {
    id: string
    label: string
    title: string
    description?: string
    bullets: string[]
    featureCards?: Array<{
        id: string
        title: string
        description?: string
        iconSvg?: string
        iconLabel?: string
    }>
    media?: VideoStoryMedia
}

export interface VideoStoryConfig {
    assets: Record<string, string>
    brand: {
        logo: string
        logoAlt: string
    }
    hero: {
        eyebrow?: string
        badges?: Array<{
            id: string
            label: string
            tone?: 'green' | 'blue' | 'yellow'
        }>
        title: string
        titleAccentFirstLine?: boolean
        description: string
        media: VideoStoryMedia
        primaryButtonText: string
        primaryAction: VideoStoryAction
        stats: Array<{
            id: string
            value: string
        }>
    }
    attention: {
        title: string
        description?: string
        items: VideoStoryCardItem[]
    }
    overview: {
        title: string
        description?: string
        paragraphs: string[]
        playbackTexts?: string[]
        media?: VideoStoryMedia
    }
    comparison: {
        title: string
        description?: string
        mobileLayout?: 'horizontal-scroll'
        highlightedColumnKey?: string
        columns: VideoStoryComparisonColumn[]
        rows: VideoStoryComparisonRow[]
    }
    advantages: {
        title: string
        description?: string
        items: VideoStoryAdvantageItem[]
    }
    testimonials: {
        title: string
        xPostUrls: string[]
    }
    faq: {
        title: string
        items: Array<{
            id?: string
            question: string
            answer: string
        }>
    }
    cta: {
        title: string
        description: string
        buttonText: string
        disclaimer?: string
        action: VideoStoryAction
    }
}

export interface VideoLandingConfig {
    page?: {
        variant?: 'default' | 'story'
        sectionsOrder?: VideoStorySectionKey[]
        showFooter?: boolean
    }
    generator: {
        modelOnlineLabel: string
        modelName: string
        modelKey: string
        title: string
        description: string
        logoText: string
        redirectUrl: string
        defaultTab: 'image' | 'text'
        requirePromptForImage: boolean
        tabs: {
            image: string
            text: string
        }
    }
    hero: {
        default: VideoHeroMaterial
        byLocale?: Record<string, VideoHeroMaterial>
    }
    communityWorks: {
        defaultFilterType: string
    }
    coreFeatures: {
        title: string
        items: Array<{
            title: string
            description: string
            url: string
        }>
    }
    howToUse: {
        title: string
        subtitle: string
        steps: Array<{
            title: string
            description: string
            icon: string
        }>
    }
    media: {
        youtubeTitle: string
        youtubeVideos: Array<{
            title: string
            embedUrl: string
        }>
        xPostsTitle: string
        twitterTweetUrls: string[]
    }
    exploreModels: {
        title: string
        items: Array<{
            to: string
            icon: string
            alt: string
            title: string
        }>
    }
    faq: {
        title: string
        items: Array<{
            question: string
            answer: string
        }>
    }
    pageDisclaimer?: string
    cta: {
        logo: string
        logoAlt: string
        title: string
        description: string
        buttonText: string
        disclaimer: string
    }
    story?: VideoStoryConfig
    seo: LandingPageSeoConfig
    analytics: {
        pageViewEvent: string
        modelName: string
    }
}

export interface ImageLandingConfig {
    generator: {
        modelOnlineLabel: string
        modelName: string
        modelKey: string
        title: string
        description: string
        logoText: string
        redirectUrl: string
        defaultTab: 'image' | 'text'
        requirePromptForImage: boolean
        imagePromptPlaceholder?: string
        useWorkbench?: boolean
        showComingSoonNotice?: boolean
        comingSoonNotice?: string
    }
    hero: {
        default: ImageHeroMaterial
        byLocale?: Record<string, Partial<ImageHeroMaterial>>
    }
    coreFeatures: {
        title: string
        subtitle?: string
        items: Array<{
            title: string
            description: string
            url: string
        }>
    }
    howToUse: {
        title: string
        subtitle?: string
        steps: Array<{
            title: string
            description: string
            icon: string
        }>
    }
    media: {
        youtubeTitle: string
        youtubeVideos: Array<{
            title: string
            embedUrl: string
        }>
        xPostsTitle: string
        twitterTweetUrls: string[]
    }
    exploreModels: {
        title: string
        subtitle?: string
        items: Array<{
            to: string
            icon: string
            alt: string
            title: string
        }>
    }
    faq: {
        title: string
        items: Array<{
            question: string
            answer: string
        }>
    }
    pageDisclaimer?: string
    cta: {
        logo: string
        logoAlt: string
        title: string
        description: string
        buttonText: string
        disclaimer: string
    }
    seo: LandingPageSeoConfig
    analytics: {
        pageViewEvent: string
        modelName: string
    }
}

export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends Array<infer U>
        ? Array<DeepPartial<U>>
        : T[K] extends object
          ? DeepPartial<T[K]>
          : T[K]
}

export interface LandingPageConfigSource<T> {
    type?: 'video' | 'image'
    id: string
    version: number
    shared: DeepPartial<T>
    locales: Record<string, DeepPartial<T>>
}

type ConfigRecord<T> = Record<string, T | LandingPageConfigSource<T>>

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]'

const cloneValue = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map(item => cloneValue(item)) as T
    }

    if (isPlainObject(value)) {
        return Object.fromEntries(
            Object.entries(value).map(([key, item]) => [key, cloneValue(item)])
        ) as T
    }

    return value
}

const mergeValue = (base: unknown, override?: unknown): unknown => {
    if (override === undefined) {
        return cloneValue(base)
    }

    if (base === undefined) {
        return cloneValue(override)
    }

    if (Array.isArray(base) || Array.isArray(override)) {
        const baseArray = Array.isArray(base) ? base : []
        const overrideArray = Array.isArray(override) ? override : []
        const maxLength = Math.max(baseArray.length, overrideArray.length)

        return Array.from({ length: maxLength }, (_, index) =>
            mergeValue(baseArray[index], overrideArray[index])
        )
    }

    if (isPlainObject(base) && isPlainObject(override)) {
        const result: Record<string, unknown> = {}
        const keys = new Set([...Object.keys(base), ...Object.keys(override)])

        for (const key of keys) {
            result[key] = mergeValue(
                (base as Record<string, unknown>)[key],
                (override as Record<string, unknown>)[key]
            )
        }

        return result
    }

    return cloneValue(override)
}

export const isLocalizedConfigSource = <T>(
    config: T | LandingPageConfigSource<T>
): config is LandingPageConfigSource<T> =>
    isPlainObject(config) && 'shared' in config && 'locales' in config

export const isVideoLandingConfig = (
    config: unknown
): config is VideoLandingConfig =>
    isPlainObject(config) &&
    'generator' in config &&
    'seo' in config &&
    'media' in config &&
    'communityWorks' in config

export const isImageLandingConfig = (
    config: unknown
): config is ImageLandingConfig =>
    isPlainObject(config) &&
    'generator' in config &&
    'seo' in config &&
    'media' in config &&
    'coreFeatures' in config

const parseJsonLikeString = (value: string): unknown | null => {
    try {
        return JSON.parse(value)
    } catch (error) {
        console.warn(
            '[landing-config] Failed to parse remote config JSON string.',
            error
        )
        return null
    }
}

const isBlobLike = (
    value: unknown
): value is Blob & { text(): Promise<string> } =>
    typeof Blob !== 'undefined' && value instanceof Blob

const decodeBinaryJsonValue = async (value: unknown): Promise<unknown | null> => {
    if (typeof value === 'string') {
        return parseJsonLikeString(value)
    }

    if (value instanceof ArrayBuffer) {
        return parseJsonLikeString(new TextDecoder().decode(new Uint8Array(value)))
    }

    if (ArrayBuffer.isView(value)) {
        return parseJsonLikeString(new TextDecoder().decode(value))
    }

    if (isBlobLike(value)) {
        return parseJsonLikeString(await value.text())
    }

    return value
}

const normalizeConfigSource = async <T>(
    value: unknown,
    validator?: (config: unknown) => config is T
): Promise<T | LandingPageConfigSource<T> | null> => {
    const normalized = await decodeBinaryJsonValue(value)

    if (
        isLocalizedConfigSource(
            normalized as T | LandingPageConfigSource<T>
        ) ||
        (validator ? validator(normalized) : false)
    ) {
        return normalized as T | LandingPageConfigSource<T>
    }

    return null
}

export const resolveLandingPageConfig = <T>(
    config: T | LandingPageConfigSource<T>,
    activeLocale: string
): T => {
    if (!isLocalizedConfigSource(config)) {
        return config
    }

    const fallbackLocaleConfig = config.locales.en || {}
    const activeLocaleConfig = config.locales[activeLocale] || {}

    return mergeValue(
        mergeValue(config.shared as T, fallbackLocaleConfig),
        activeLocaleConfig
    ) as T
}

const getLocalConfig = <T>(
    modelId: string,
    localConfigs: ConfigRecord<T>
): T | LandingPageConfigSource<T> | null =>
    localConfigs[`./${modelId}.json`] ||
    localConfigs[`./${modelId.toLowerCase()}.json`] ||
    null

export const loadLandingPageConfigSource = async <T>(
    modelId: string,
    localConfigs: ConfigRecord<T>,
    remoteBaseUrl?: string,
    validator?: (config: unknown) => config is T
): Promise<T | LandingPageConfigSource<T> | null> => {
    const normalizedBaseUrl = remoteBaseUrl?.trim().replace(/\/$/, '')

    if (normalizedBaseUrl) {
        try {
            const remoteConfig = await $fetch<
                string | T | LandingPageConfigSource<T>
            >(`${normalizedBaseUrl}/${encodeURIComponent(modelId)}.json`)

            const normalizedRemoteConfig = await normalizeConfigSource(
                remoteConfig,
                validator
            )
            if (normalizedRemoteConfig) {
                return normalizedRemoteConfig
            }

            console.warn(
                `[landing-config] Remote config for "${modelId}" has invalid shape, fallback to local config.`
            )
        } catch (error) {
            const statusCode =
                typeof error === 'object' && error && 'statusCode' in error
                    ? String((error as { statusCode?: number }).statusCode)
                    : ''
            const statusMessage =
                typeof error === 'object' && error && 'statusMessage' in error
                    ? String((error as { statusMessage?: string }).statusMessage)
                    : ''

            if (statusCode === '403' || statusCode === '404') {
                console.warn(
                    `[landing-config] Remote config "${modelId}" unavailable (${statusCode} ${statusMessage || 'fallback'}), fallback to local config.`
                )
            } else {
                console.warn(
                    `[landing-config] Remote config load failed for "${modelId}", fallback to local config.`,
                    error
                )
            }
        }
    }

    const localConfig = getLocalConfig(modelId, localConfigs)
    if (!localConfig) {
        return null
    }

    return localConfig
}

export const loadLandingPageConfigFromUrl = async <T>(
    remoteUrl: string | undefined,
    localConfig: T | LandingPageConfigSource<T> | null,
    validator?: (config: unknown) => config is T
): Promise<T | LandingPageConfigSource<T> | null> => {
    const normalizedUrl = remoteUrl?.trim()

    if (normalizedUrl) {
        try {
            const remoteConfig = await $fetch<
                string | T | LandingPageConfigSource<T>
            >(normalizedUrl)

            const normalizedRemoteConfig = await normalizeConfigSource(
                remoteConfig,
                validator
            )

            if (normalizedRemoteConfig) {
                return normalizedRemoteConfig
            }

            console.warn(
                `[landing-config] Remote config "${normalizedUrl}" has invalid shape, fallback to local config.`
            )
        } catch (error) {
            const statusCode =
                typeof error === 'object' && error && 'statusCode' in error
                    ? String((error as { statusCode?: number }).statusCode)
                    : ''
            const statusMessage =
                typeof error === 'object' && error && 'statusMessage' in error
                    ? String((error as { statusMessage?: string }).statusMessage)
                    : ''

            if (statusCode === '403' || statusCode === '404') {
                console.warn(
                    `[landing-config] Remote config "${normalizedUrl}" unavailable (${statusCode} ${statusMessage || 'fallback'}), fallback to local config.`
                )
            } else {
                console.warn(
                    `[landing-config] Remote config load failed for "${normalizedUrl}", fallback to local config.`,
                    error
                )
            }
        }
    }

    return localConfig
}
